'use client'

import usePostTrialApplyStepViewedEvent from '@components/hooks/usePostTrialApplyStepViewedEvent'
import { trackEvent } from '@lib/tracking'
import { isInAppBrowser } from '@lib/tracking/isInAppBrowser'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import type { ApplySuccessPageProps } from 'app/p/apply-success/[token]/[step]/page'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { getApplySuccessRouteToRedirectTo } from './getApplySuccessRouteToRedirectTo'
import { PatientCallToSiteInteraction } from './PatientCallToSite/PatientCallToSiteInteraction.client'
import { PatientCallToSiteIntro } from './PatientCallToSite/PatientCallToSiteIntro'

export const consoleLogger = console

export function PatientCallToSiteStep(props: ApplySuccessPageProps) {
  const {
    idToken: trialApplicationIdToken,
    mostQualifiedTrialApplicationIdToken,
    step,
  } = props

  const router = useRouter()
  const [microphoneAccessGranted, setMicrophoneAccessGranted] = useState(false)

  useEffect(() => {
    if (isInAppBrowser()) {
      trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_IN_APP_BROWSER_ENTERED, {
        trialApplicationIdToken,
      })
    }

    const fetchMicrophonePermission = async () => {
      // Some browsers don't support the Permissions API, so automatically skip accepting microphone access
      if (!('permissions' in navigator)) {
        consoleLogger.warn('No Permissions API, so disabling microphone access')
        setMicrophoneAccessGranted(false)
        return
      }

      try {
        const result = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        })
        setMicrophoneAccessGranted(result.state === 'granted')
      } catch (err) {
        consoleLogger.error('Error checking microphone permission:', err)
        setMicrophoneAccessGranted(false)
      }
    }
    fetchMicrophonePermission()
  }, [])

  usePostTrialApplyStepViewedEvent({
    mostQualifiedTrialApplicationIdToken,
    trialApplicationIdToken,
    type: step,
  })

  const handleStartCall = () => {
    trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_START_BUTTON_CLICKED, {
      trialApplicationIdToken,
    })
  }

  const handleNextButtonClick = () => {
    trackEvent(TrackingEvents.PATIENT_CALL_TO_SITE_NEXT_BUTTON_CLICKED, {
      trialApplicationIdToken,
    })
    router.push(getApplySuccessRouteToRedirectTo(step, props))
  }

  return (
    <>
      {/* For mobile, we only allow tel URI click - so we should not display two different UIs based on mic access */}
      <div className='block md:hidden'>
        <PatientCallToSiteIntro
          {...props}
          handleNextButtonClick={handleNextButtonClick}
          microphoneAccessGranted={microphoneAccessGranted}
          onStartCall={handleStartCall}
          setMicrophoneAccessGranted={setMicrophoneAccessGranted}
        />
      </div>
      {/* For desktop, we show the dial pad if microphone access is granted */}
      <div className='hidden md:block'>
        {microphoneAccessGranted ? (
          <PatientCallToSiteInteraction {...props} />
        ) : (
          <PatientCallToSiteIntro
            {...props}
            handleNextButtonClick={handleNextButtonClick}
            microphoneAccessGranted={microphoneAccessGranted}
            onStartCall={handleStartCall}
            setMicrophoneAccessGranted={setMicrophoneAccessGranted}
          />
        )}
      </div>
    </>
  )
}
